import { Suspense, lazy } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NodeProperty } from "@/components/doc/right-sidebar/node-viewer/NodeProperty";
import { Value } from "@/components/expression/utils/layout";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { getDerivedUnitText } from "@/components/suggestions/providers/utils/getPhantomHints";
import { isNodeEditable } from "@/components/tiptap/lock/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";

// Lazy to avoid circular dep: InlineNode → SingleRowLayout → Row → ExpressionNode → InlineNode
const LazyInlineNodeComponent = lazy(() =>
  import("@/components/expression/InlineNode").then((m) => ({
    default: m.InlineNodeComponent,
  })),
);

type Props = {
  node: VariableNodeModel | ConstantNodeModel;
};

export const NodeUnitProperty = observer(function NodeUnitProperty({ node }: Props) {
  const t = useTranslations("sidebar");

  const inlineNodes = node.unitNode?.inlineNode?.nodes;
  const derivedText =
    !inlineNodes && node instanceof VariableNodeModel
      ? getDerivedUnitText(node.doc, node.labeledValue)
      : null;

  if (!inlineNodes && !derivedText) return null;

  return (
    <NodeProperty
      name={t("nodeProps.nodeUnit")}
      value={
        <NodeStyleProvider renderContext="right-sidebar" size="sm" text="unit" view="text">
          {inlineNodes ? (
            <Suspense>
              <LazyInlineNodeComponent
                doc={node.doc}
                nodes={inlineNodes}
                className="pointer-events-none font-mono"
              />
            </Suspense>
          ) : (
            <Value isReadable className="pointer-events-none font-mono">
              {derivedText}
            </Value>
          )}
        </NodeStyleProvider>
      }
      hideToggle
      readOnly={!isNodeEditable(node)}
    />
  );
});
